import { userSchema } from "@/services/users/schema";
import { ChannelType, MemberRole } from "@prisma/client";
import { z } from "zod";

export const DEFAULT_CHANNEL_PREFIX = "pw-chat-";

export type RoomType = "video" | "voice" | "text" | "stream";

export const channelUserSchema = userSchema
  .pick({
    name: true,
    image: true,
  })
  .extend({
    id: z.string().cuid(),
  });
const memberSchema = z
  .object({
    user: channelUserSchema,
    role: z.nativeEnum(MemberRole),
  })
  .transform((data) => ({ ...data.user, role: data.role }));
export type Member = z.infer<typeof memberSchema>;

const dateSchema = z.union([z.coerce.date(), z.date()]);

const readBySchema = z.object({
  readAt: dateSchema.nullish(),
  userId: z.string().cuid(),
});

const baseMessageSchema = z.object({
  id: z.string().cuid(),
  user: memberSchema,
  content: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema.nullish(),
  deletedAt: dateSchema.nullish(),
  readBy: z.array(readBySchema).optional().default([]),
  editedAt: z.coerce.date().nullish(),
  _count: z
    .object({
      replies: z.number(),
    })
    .optional(),
});
export const messageSchema = baseMessageSchema.transform(
  ({ _count, ...data }) => {
    return {
      ...data,
      replyCount: _count?.replies ?? 0,
    };
  },
);

export const messageWithRepliesSchema = baseMessageSchema.extend({
  replies: z.array(messageSchema),
});

export const messagesSchema = z.array(messageSchema);
export type Message = z.infer<typeof messageSchema>;

export const channelSchema = z.object({
  id: z.string().cuid(),
  name: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema.nullish(),
  deletedAt: dateSchema.nullish(),
  members: z.array(memberSchema).optional().default([]),
  messages: messagesSchema.optional().default([]),
  type: z.nativeEnum(ChannelType),
});
export const channelsSchema = z.array(channelSchema);

export type Channel = z.infer<typeof channelSchema>;

export const chatSchema = z.object({
  channel: channelSchema,
});

export function parseChannel(data: unknown) {
  const result = channelSchema.safeParse(data);

  return result.success ? result.data : null;
}

export function parseChannelUser(data: unknown) {
  const result = channelUserSchema.safeParse(data);

  return result.success ? result.data : null;
}
